import {createFileRoute, useRouter} from '@tanstack/react-router';
import {useState} from 'react';
import {Typography} from '@/components/ui/typography';
import {toastManager} from '@/components/ui/toast';
import {CrcaConnectionCard} from '@/components/connections/crca/crcaConnectionCard';
import {
  CrcaLinkModal,
  type CrcaLinkModalState,
} from '@/components/connections/crca/crcaLinkModal';
import {CrcaUnlinkConfirm} from '@/components/connections/crca/crcaUnlinkConfirm';
import {ComingSoonCard} from '@/components/connections/comingSoonCard';
import {IdleConnectionCard} from '@/components/connections/idleConnectionCard';
import crca1x from '@/components/connections/crca/crca_logo@1x.jpg';
import crca2x from '@/components/connections/crca/crca_logo@2x.jpg';
import usac1x from './usac_logo@1x.jpg';
import usac2x from './usac_logo@2x.jpg';
import rusa1x from './rusa_logo@1x.jpg';
import rusa2x from './rusa_logo@2x.jpg';
import {getProfileExternalIds} from '@/server/profileExternalId';
import {
  getCrcaMembership,
  confirmCrcaLink,
  unlinkCrcaMembership,
} from '@/server/crcaMembership';

export const Route = createFileRoute('/dashboard/connections')({
  loader: () => getProfileExternalIds(),
  component: ConnectionsPage,
});

const CRCA_INTEGRATION_KEY = 'crca';

/**
 * Dashboard page for connecting governing-body and ride-tracking accounts.
 * Today only CRCA is wired up; USAC and RUSA render as "Coming Soon"
 * placeholders.
 */
function ConnectionsPage() {
  const externalIds = Route.useLoaderData();
  const router = useRouter();

  const crcaLink =
    externalIds.find((x) => x.integrationKey === CRCA_INTEGRATION_KEY) ?? null;

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkState, setLinkState] = useState<CrcaLinkModalState>('input');
  const [foundName, setFoundName] = useState<string | null>(null);
  const [pendingMemberId, setPendingMemberId] = useState<string | null>(null);
  const [unlinkOpen, setUnlinkOpen] = useState(false);

  const resetLinkModal = () => {
    setLinkState('input');
    setFoundName(null);
    setPendingMemberId(null);
  };

  const handleOpenLink = () => {
    resetLinkModal();
    setLinkModalOpen(true);
  };

  const handleLookup = async (memberId: string) => {
    setLinkState('loading');
    setPendingMemberId(memberId);
    try {
      const result = await getCrcaMembership({data: {memberId}});
      if (result.ok) {
        setFoundName(result.name);
        setLinkState('found');
      } else {
        setFoundName(null);
        setLinkState('notFound');
      }
    } catch (error) {
      console.error('[connections] getCrcaMembership failed', error);
      toastManager.add({
        title: 'CRCA lookup failed. Please try again.',
        type: 'error',
      });
      setLinkState('input');
    }
  };

  const handleConfirm = async () => {
    if (!pendingMemberId || !foundName) return;
    try {
      await confirmCrcaLink({data: {memberId: pendingMemberId}});
      toastManager.add({
        title: `Linked CRCA membership as ${foundName}`,
        type: 'success',
      });
      setLinkModalOpen(false);
      resetLinkModal();
      router.invalidate();
    } catch (error) {
      console.error('[connections] confirmCrcaLink failed', error);
      toastManager.add({
        title: 'Could not link CRCA membership. Please try again.',
        type: 'error',
      });
    }
  };

  const handleUnlink = async () => {
    try {
      await unlinkCrcaMembership();
      toastManager.add({
        title: 'CRCA membership unlinked',
        type: 'success',
      });
      setUnlinkOpen(false);
      router.invalidate();
    } catch (error) {
      console.error('[connections] unlinkCrcaMembership failed', error);
      toastManager.add({
        title: 'Could not unlink CRCA membership. Please try again.',
        type: 'error',
      });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <Typography level="H3">Connections</Typography>
        <Typography level="B2" className="text-neutral-500">
          Link your governing-body license, club memberships, and ride-tracking
          apps. Linking unlocks events and centralizes them in Wayra.
        </Typography>
      </header>

      <div className="flex flex-col gap-3">
        {crcaLink ? (
          <CrcaConnectionCard
            link={crcaLink}
            onUnlink={() => setUnlinkOpen(true)}
          />
        ) : (
          <IdleConnectionCard
            name="Century Road Club Association"
            description="Link your CRCA membership to register for club races and clinics."
            logoSrc1x={crca1x}
            logoSrc2x={crca2x}
            buttonLabel="Link membership"
            onLink={handleOpenLink}
          />
        )}
        <ComingSoonCard
          name="USA Cycling"
          description="Connect your USAC license to unlock road, gravel, and cyclocross events."
          logoSrc1x={usac1x}
          logoSrc2x={usac2x}
        />
        <ComingSoonCard
          name="Randonneurs USA"
          description="Connect your RUSA membership to track brevets and randonneuring events."
          logoSrc1x={rusa1x}
          logoSrc2x={rusa2x}
        />
      </div>

      <CrcaLinkModal
        open={linkModalOpen}
        state={linkState}
        foundName={foundName}
        onLookup={handleLookup}
        onConfirm={handleConfirm}
        onTryAgain={() => {
          resetLinkModal();
        }}
        onCancel={() => {
          setLinkModalOpen(false);
          resetLinkModal();
        }}
      />

      <CrcaUnlinkConfirm
        open={unlinkOpen}
        onConfirm={handleUnlink}
        onCancel={() => setUnlinkOpen(false)}
      />
    </div>
  );
}
